//@ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  TextField,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Select,
  Autocomplete,
  Tabs,
  Tab,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useGetToken } from "../../hooks/useGetToken";
import axios from "axios";
import { useGlobalStateContext } from "../../context/globalState";
import { useTranslation } from "react-i18next";
import StyledButtons from "../../styledComponents/styledButtons";
import { calulateExpiryDate, QUALIFICATION_LEVEL_ORDER } from "../helper";
import { QualificationsType as SelectedQualificationsType } from "../../context";
import TextLabel from "../TextLabel";
import StyledTextField from "../../styledComponents/styledTextField";
import StyledInputLabel from "../../styledComponents/styledInputLabel";
import { CONSTANTS } from "../../CONSTANTS";
import Loading from "../Loading";
import AlertBox from "../AlertBox";
import MethodModulesList from "./MethodModulesList";

// Training-type color palette (background + thin border)
const TYPE_COLORS = {
  GLOBAL_SAFETY: { bg: "#F6F8FF", border: "#B8B6E0" },
  LOCAL_SAFETY: { bg: "#FEF8E7", border: "#D6A533" },
  METHOD: { bg: "#DFDEFD", border: "#918DF6" },
} as const;

export type QualificationType = {
  qualificationTypeId: number;
  qualificationType: string;
};

const QualificationsApp: React.FC<{
  onClose: () => void;
  countryList?: any[];
}> = ({
  onClose,
  previousQualifications,
  selectedTrainings,
  onCheckboxChange,
  profileFormData,
  countryList: propCountryList,
}) => {
  // Reading from global state context
  const {
    countryList: globalCountryList,
    loading,
    beginLoading,
    endLoading,
    userFirstName,
    userLastName,
    qualificationsList,
    setQualificationsList,
    passportTypeId,
    setPassportTypeId,
    qualificationTypeId,
    setQualificationTypeId,
    trainingTypes,
    country,
    userPreference: { languagePreference },
    selectedBusinessTypeId,
  } = useGlobalStateContext();

  // Always use passed countryList from AdminInstallerForm
  const countryList = propCountryList || [];

  const { t } = useTranslation();

  const [selectedQualifications, setSelectedQualifications] =
    useState<SelectedQualificationsType>([]);
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    GlobalSafety: false,
    LocalSafety: false,
    Method: false,
  });
  const [qualifications, setQualification] = useState([]);
  const [activeLevelTab, setActiveLevelTab] = useState<string>("All");
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  // Handle Error Alert Box
  const [alertBoxState, setAlertBoxState] = useState({
    type: "",
    message: "",
  });
  // const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [getToken] = useGetToken();
  const [passportTypes, setPassportTypes] = useState([]);
  // Initialize selectedCountry with the default country from props or global state
  const [selectedCountry, setSelectedCountry] = useState<string>(
    profileFormData?.country ||
      profileFormData?.country_code ||
      country?.[0] ||
      "",
  );
  const [selectedCountryObject, setSelectedCountryObject] = useState<any>(null);
  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };
  const [qualificationTypeList, setQualificationTypeList] = useState<
    QualificationType[]
  >([]);

  useEffect(() => {
    const fetchPassportTypeId = async () => {
      const token = await getToken();
      const loadingToken = beginLoading("qual-app-passport-types");
      try {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        };

        const response = await axios.get(
          `${process.env.REACT_APP_API_ENDPOINT}/v1/passportTypes?lang=${languagePreference}`,
          config,
        );

        setPassportTypes(response.data);
        setPassportTypeId(""); // <-- clear selection by default
      } catch (error) {
        console.error("Error fetching passportTypeId:", error);
      } finally {
        endLoading(loadingToken);
      }
    };

    fetchPassportTypeId();
  }, []);

  useEffect(() => {
    // If passportTypeId changes, fetch the qualificationType list based on the new passport level
    if (passportTypeId) {
      fetchQualificationTypeList(passportTypeId);
    }
  }, [passportTypeId]);

  useEffect(() => {
    if (qualificationsList.length === 0) return;
    setSelectedQualifications((prev) =>
      prev.length === 0 ? [...qualificationsList] : prev,
    );
  }, [qualificationsList]);

  useEffect(() => {
    if (!qualifications.length) return;
    setSelectedQualifications((prev) => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map((q) => {
        if (!q || q.qualificationLevel !== undefined) return q;
        const match = qualifications.find((t) => t.trainingId === q.trainingId);
        // Skip if no match, or if the match itself has no level metadata
        // (e.g., Global Safety / Local Requirements rows). Returning `q`
        // preserves the reference so the array isn't pointlessly rebuilt.
        if (!match || match.qualificationLevel == null) return q;
        changed = true;
        return {
          ...q,
          qualificationLevel: match.qualificationLevel,
          qualificationLevelId: match.qualificationLevelId,
        };
      });
      return changed ? next : prev;
    });
  }, [qualifications]);

  useEffect(() => {
    // Fetch Trainings List if passportTypeId and qualificationTypeId are available
    if (passportTypeId && qualificationTypeId) {
      fetchQualificationData(
        qualificationTypeId,
        selectedCountry,
        selectedBusinessTypeId || undefined,
      );
    }
  }, [
    passportTypeId,
    qualificationTypeId,
    selectedCountry,
    selectedBusinessTypeId,
  ]);

  const toggleQualification = (
    qualification: string,
    type: string,
    trainingId: string,
    qualificationTypeId: number,
    validForYears?: number | null,
    qualificationLevel?: string,
    qualificationLevelId?: number | null,
  ) => {
    if (selectedQualifications.find((qual) => qual.trainingId === trainingId)) {
      setSelectedQualifications((prev) =>
        prev.filter((qual) => qual.trainingId !== trainingId),
      );
    } else {
      setSelectedQualifications((prev) => [
        ...prev,
        {
          title: qualification,
          completionDate: new Date().toISOString().split("T")[0],
          expirationDate: calulateExpiryDate(
            type,
            trainingTypes,
            validForYears,
          ),
          type,
          trainingId,
          // qualificationTypeId,
          validForYears,
          qualificationLevel,
          qualificationLevelId,
        },
      ]);
    }
  };

  const fetchQualificationData = async (
    qualificationTypeId,
    country = "",
    businessTypeId?: number,
  ) => {
    const token = await getToken();
    const loadingToken = beginLoading("qual-app-qualifications");
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const countryCondition = country ? `&verifiedCountryId=${country}` : ""; // Add country condition if available
      const businessTypeCondition = businessTypeId
        ? `&businessTypeId=${businessTypeId}`
        : ""; // Add business type condition if available
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/trainings/trainingsByType?lang=${languagePreference}&qualificationTypeId=${qualificationTypeId}${countryCondition}${businessTypeCondition}`,
        config,
      );
      setQualification(response.data);
    } catch (error) {
      console.error("Error fetching qualification:", error);
    } finally {
      endLoading(loadingToken);
    }
  };

  const fetchQualificationTypeList = async (value) => {
    const token = await getToken();
    const loadingToken = beginLoading("qual-app-qual-types");
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/qualificationTypes?passportType=${value}&lang=${languagePreference}&businessTypeId=${selectedBusinessTypeId || null}`,
        config,
      );
      setQualificationTypeList(response.data);
      qualificationTypeId && setQualificationTypeId(""); // <-- clear selection by default
    } catch (error) {
      console.error("Error fetching qualificationTypes:", error);
    } finally {
      endLoading(loadingToken);
    }
  };

  const handleDeleteQualification = (trainingId: number) => {
    if (selectedQualifications.find((qual) => qual.trainingId === trainingId)) {
      setSelectedQualifications((prev) =>
        prev.filter((qual) => qual.trainingId !== trainingId),
      );
    }
  };

  const validateDate = useCallback(
    (
      date: string | null,
      type: "completionDate" | "expirationDate",
      qualificationId: number,
      qualificationsList = selectedQualifications,
    ) => {
      if (!date) return { valid: true, message: "" };

      const currentDate = new Date();
      const dateObj = new Date(date);

      const correspondingQualification = qualificationsList.find(
        (qual) => qual.trainingId === qualificationId,
      );
      const completionDate = correspondingQualification?.completionDate
        ? new Date(correspondingQualification.completionDate)
        : null;
      const expirationDate = correspondingQualification?.expirationDate
        ? new Date(correspondingQualification.expirationDate)
        : null;

      if (type === "completionDate") {
        if (dateObj > currentDate) {
          return {
            valid: false,
            message: t("validation.completionDateFuture"),
          };
        }

        if (expirationDate && expirationDate < dateObj) {
          return {
            valid: false,
            message: "",
          };
        }
      }

      if (type === "expirationDate") {
        if (completionDate && dateObj < completionDate) {
          return {
            valid: false,
            message: t("validation.expirationDateBeforeCompletion"),
          };
        }
      }

      return { valid: true, message: "" };
    },
    [selectedQualifications],
  );

  const handleDateChange = (
    qualificationId: number,
    type: "completionDate" | "expirationDate",
    date: string | null,
  ) => {
    // update selected qualifications
    const modifiedQualifications = selectedQualifications.map((qual) => {
      if (qual.trainingId !== qualificationId) return qual;

      // When completion date changes, recalculate expiration date
      // Use today as effectiveDate when date is empty (e.g. "Clear" button clicked)
      if (type === "completionDate") {
        const effectiveDate = date || new Date().toISOString().split("T")[0];
        const recalculatedExpiration = calulateExpiryDate(
          qual.type,
          trainingTypes,
          qual.validForYears,
          effectiveDate,
        );
        return {
          ...qual,
          completionDate: effectiveDate,
          expirationDate: recalculatedExpiration || qual.expirationDate,
        };
      }

      return { ...qual, [type]: date };
    });
    setSelectedQualifications(modifiedQualifications);

    // validate the changed field using modifiedQualifications
    const { valid, message } = validateDate(
      date,
      type,
      qualificationId,
      modifiedQualifications,
    );

    if (!valid) {
      setValidationErrors((prev) => ({
        ...prev,
        [type]: { ...prev[type], [qualificationId]: message },
      }));
    } else {
      setValidationErrors((prev) => {
        const updatedErrors = { ...prev };
        if (updatedErrors[type]) {
          delete updatedErrors[type][qualificationId];
          if (Object.keys(updatedErrors[type]).length === 0) {
            delete updatedErrors[type];
          }
        }
        return updatedErrors;
      });
    }

    // 🔄 re-validate the dependent field
    const otherType =
      type === "completionDate" ? "expirationDate" : "completionDate";
    const otherDate = modifiedQualifications.find(
      (q) => q.trainingId === qualificationId,
    )?.[otherType] as string | null;

    if (otherDate) {
      const { valid: otherValid, message: otherMessage } = validateDate(
        otherDate,
        otherType,
        qualificationId,
        modifiedQualifications,
      );

      if (!otherValid) {
        setValidationErrors((prev) => ({
          ...prev,
          [otherType]: { ...prev[otherType], [qualificationId]: otherMessage },
        }));
      } else {
        setValidationErrors((prev) => {
          const updatedErrors = { ...prev };
          if (updatedErrors[otherType]) {
            delete updatedErrors[otherType][qualificationId];
            if (Object.keys(updatedErrors[otherType]).length === 0) {
              delete updatedErrors[otherType];
            }
          }
          return updatedErrors;
        });
      }
    }
  };

  const handleCancel = () => {
    handleCloseAlert(); // Close the alert box if open
    setSelectedQualifications([]); // Revert to previous saved state or empty if none
    onClose();
  };

  const handleSubmit = () => {
    handleCloseAlert(); // Close the alert box if open
    const hasValidationErrors = Object.keys(validationErrors).some((key) => {
      return Object.keys(validationErrors[key] || {}).some((id) =>
        selectedQualifications.some((qual) => qual.trainingId === parseInt(id)),
      );
    });

    if (hasValidationErrors) {
      // If there are validation errors for existing qualifications, do not proceed with submission
      setAlertBoxState({
        type: "error",
        message: t("validation.fixErrorsBeforeSaving"),
      });
      return;
    }

    setQualificationsList(selectedQualifications); // Update qualifications list in global state
    setSelectedQualifications([]); // Revert to previous saved state or empty if none
    onClose(); // Close the modal or perform any other action
  };

  const handleChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;

    if (name === "passportTypeId") {
      setPassportTypeId(value); // Update passport level in global state
      fetchQualificationTypeList(value); // Update qualification types based on passport level
      // Reset the level tab filter - otherwise a previously selected level
      // (e.g. "Basic") stays active and hides all other-level trainings
      // (Global Safety, Local Requirements, etc.) for the newly selected
      // passport type, even though the tab strip itself may change/reset.
      setActiveLevelTab("All");
    }

    if (name === "qualificationType") {
      setQualificationTypeId(value); // Update qualification type in global state
    }
  };

  const handleSearch = (event: React.ChangeEvent<{}>, value: any) => {
    // Handle country selection change - API expects country_code
    if (value && value.country_code) {
      setSelectedCountry(value.country_code);
      setSelectedCountryObject(value);
    } else {
      setSelectedCountry("");
      setSelectedCountryObject(null);
    }
  };

  const getUserName = () => {
    if (userFirstName && userLastName) {
      return `${userFirstName} ${userLastName}`;
    } else if (userFirstName) {
      return userFirstName;
    } else if (userLastName) {
      return userLastName;
    } else {
      return t("passportPage.labels.installer");
    }
  };

  // Function to handle the close event of the alert box
  const handleCloseAlert = () => {
    setAlertBoxState({ type: "", message: "" });
  };

  // Initialize selected country object when component mounts or countryList changes
  useEffect(() => {
    console.log("Country List = ", countryList);
    console.log("Profile form data = ", profileFormData);
    if (
      countryList &&
      countryList.length > 0 &&
      (profileFormData?.country || profileFormData?.countryId) &&
      !selectedCountryObject
    ) {
      // Find country by country_id from profileFormData
      const defaultCountry = countryList.find(
        (c) =>
          String(c.country_id) ===
          String(profileFormData.country || profileFormData.countryId),
      );
      if (defaultCountry && defaultCountry.country_code) {
        setSelectedCountryObject(defaultCountry);
        setSelectedCountry(defaultCountry.country_code);
      }
    }
  }, [countryList, profileFormData?.country]);

  // Tabs come ONLY from levels carried by saved/selected trainings.
  // The current qual type's API trainings do NOT contribute — a tab appears
  // only when the installer actually has a training tagged to that level.
  // Sorted via QUALIFICATION_LEVEL_ORDER so Start appears before Basic before
  // Intermediate before Specialist, regardless of DB id.
  const availableLevels = (() => {
    const levels = new Set<string>();
    selectedQualifications.forEach((q) => {
      if (q?.qualificationLevel) levels.add(q.qualificationLevel);
    });
    return Array.from(levels).sort(
      (a, b) =>
        (QUALIFICATION_LEVEL_ORDER[a] ?? Number.MAX_SAFE_INTEGER) -
        (QUALIFICATION_LEVEL_ORDER[b] ?? Number.MAX_SAFE_INTEGER),
    );
  })();

  // If the active level tab no longer exists (e.g., user removed last row of that
  // level), fall back to "All" so the tab strip stays consistent.
  useEffect(() => {
    if (activeLevelTab !== "All" && !availableLevels.includes(activeLevelTab)) {
      setActiveLevelTab("All");
    }
    // availableLevels is recomputed each render - depending on its identity
    // would loop. Depend on the joined level names instead.
  }, [availableLevels.join("|"), activeLevelTab]);

  // Tab status reflects only the saved `state` field returned by the GET API.
  // Unsaved edits (date changes, newly added rows) do not affect tab icons
  // the admin sees the persisted status until they save and the data refreshes.
  const getTabStatus = (
    level: string,
  ): "Active" | "Warning" | "Expired" | null => {
    const rows =
      level === "All"
        ? selectedQualifications
        : selectedQualifications.filter(
            (q) => q && q.qualificationLevel === level,
          );
    if (rows.length === 0) return null;
    const states = rows.map((r) => r?.state);
    if (states.includes("Expired")) return "Expired";
    if (states.includes("Warning")) return "Warning";
    if (states.includes("Active")) return "Active";
    return null;
  };

  // Filter by active tab, then cluster same-type rows together (sorted by
  // trainingTypeId so GS / LR / Method appear as contiguous bands).
  const visibleQualifications = selectedQualifications
    .filter(
      (qualification) =>
        qualification &&
        (activeLevelTab === "All" ||
          qualification.qualificationLevel === activeLevelTab),
    )
    .sort((a, b) => {
      if (!a || !b) return 0;
      if (a.type !== b.type) return Number(a.type) - Number(b.type);
      return (a.title || "").localeCompare(b.title || "");
    });

  const tabStatusIcon = (status: "Active" | "Warning" | "Expired" | null) => {
    if (!status) return null;
    const src =
      status === "Expired"
        ? "/icon_expired.svg"
        : status === "Warning"
          ? "/icon_expiringsoon.svg"
          : "/icon_valid.svg";
    return (
      <img
        src={src}
        alt={status}
        style={{ width: 16, height: 16, marginRight: 6 }}
      />
    );
  };

  return (
    <>
      {loading && <Loading />}
      {alertBoxState.type && (
        <div className="alert-box">
          <AlertBox
            type={alertBoxState.type}
            message={alertBoxState.message}
            onCloseCb={handleCloseAlert}
          />
        </div>
      )}
      <Box display="flex" gap={2} padding="24px" style={{ overflow: "hidden" }}>
        {/* Available Qualifications Accordion */}
        {passportTypeId && qualificationTypeId ? (
          <Box
            flex={1}
            style={{
              background: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #ccc",
              padding: "16px",
              height: "80vh",
              width: "max-content",
              overflowY: "auto",
              maxWidth: "20vw",
            }}
          >
            <TextLabel
              type="titleRegular"
              gutterBottom
              sxStyles={{
                textAlign: "left",
                opacity: 1,
              }}
            >
              {t(
                "adminDashboard.form.addInstaller.labels.qualifications.availableQualifications",
              )}
            </TextLabel>

            <Autocomplete
              id="search"
              options={countryList || []}
              value={selectedCountryObject}
              getOptionDisabled={(option) =>
                option.status === CONSTANTS.STATUS.INACTIVE
              }
              sx={{ width: "100%", marginBottom: 2, height: "40px" }}
              onChange={handleSearch}
              autoHighlight
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                return option?.name || "";
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.country_id === value.country_id;
              }}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label={t(
                    "adminDashboard.form.addInstaller.labels.qualifications.countrySearch",
                  )}
                  InputProps={{
                    ...params.InputProps,
                    autoComplete: "new-password",
                  }}
                />
              )}
            />

            <Box>
              <ListItem
                button
                onClick={() => toggleSection("GlobalSafety")}
                sx={{
                  backgroundColor: TYPE_COLORS.GLOBAL_SAFETY.bg,
                  border: `1px solid ${TYPE_COLORS.GLOBAL_SAFETY.border}`,
                  borderRadius: 2,
                  marginBottom: 1.5,
                }}
              >
                <ListItemText
                  primary={t(
                    "adminDashboard.form.updateInstaller.labels.qualifications.globalSafety",
                  )}
                />
                {expandedSections.GlobalSafety ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ListItem>
              <Collapse in={expandedSections.GlobalSafety}>
                <List>
                  {qualifications
                    .filter(
                      (item) =>
                        item.trainingTypeId === trainingTypes.GLOBAL_SAFETY,
                    )
                    .sort((a, b) =>
                      a.trainingName.localeCompare(b.trainingName),
                    )
                    .map((item) => (
                      <ListItem key={item.trainingId}>
                        <Checkbox
                          checked={
                            !!selectedQualifications?.find(
                              (qual) => qual.trainingId === item.trainingId,
                            )
                          }
                          onChange={() =>
                            toggleQualification(
                              item.trainingName,
                              trainingTypes.GLOBAL_SAFETY,
                              item.trainingId,
                              item.qualificationTypeId,
                              item.validForYears,
                            )
                          }
                        />
                        <ListItemText primary={item.trainingName} />
                      </ListItem>
                    ))}
                </List>
              </Collapse>
            </Box>

            {/* Local Safety Section */}
            <Box>
              <ListItem
                button
                onClick={() => toggleSection("LocalSafety")}
                sx={{
                  backgroundColor: TYPE_COLORS.LOCAL_SAFETY.bg,
                  border: `1px solid ${TYPE_COLORS.LOCAL_SAFETY.border}`,
                  borderRadius: 2,
                  marginBottom: 1.5,
                }}
              >
                <ListItemText
                  primary={t(
                    "adminDashboard.form.updateInstaller.labels.qualifications.localSafety",
                  )}
                />
                {expandedSections.LocalSafety ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ListItem>
              <Collapse in={expandedSections.LocalSafety}>
                <List>
                  {qualifications
                    .filter(
                      (item) =>
                        item.trainingTypeId === trainingTypes.LOCAL_SAFETY,
                    )
                    .sort((a, b) =>
                      a.trainingName.localeCompare(b.trainingName),
                    )
                    .map((item) => (
                      <ListItem key={item.trainingId}>
                        <Checkbox
                          checked={
                            !!selectedQualifications?.find(
                              (qual) => qual.trainingId === item.trainingId,
                            )
                          }
                          onChange={() =>
                            toggleQualification(
                              item.trainingName,
                              trainingTypes.LOCAL_SAFETY,
                              item.trainingId,
                              item.qualificationTypeId,
                              item.validForYears,
                            )
                          }
                        />
                        <ListItemText primary={item.trainingName} />
                      </ListItem>
                    ))}
                </List>
              </Collapse>
            </Box>

            {/* Method Section */}
            <Box>
              <ListItem
                button
                onClick={() => toggleSection("Method")}
                sx={{
                  backgroundColor: TYPE_COLORS.METHOD.bg,
                  border: `1px solid ${TYPE_COLORS.METHOD.border}`,
                  borderRadius: 2,
                  marginBottom: 1.5,
                }}
              >
                <ListItemText
                  primary={t(
                    "adminDashboard.form.updateInstaller.labels.qualifications.method",
                  )}
                />
                {expandedSections.Method ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ListItem>
              <Collapse in={expandedSections.Method}>
                <MethodModulesList
                  qualifications={qualifications}
                  selectedQualifications={selectedQualifications}
                  toggleQualification={toggleQualification}
                />
                {/* <List>
                {qualifications
                  .filter((item) => item.trainingTypeId === trainingTypes.METHOD)
                  .sort((a, b) => a.trainingName.localeCompare(b.trainingName))
                  .map((item) => (
                    <ListItem key={item.trainingId}>
                      <Checkbox
                        checked={!!selectedQualifications?.find((qual) => qual.trainingId === item.trainingId)}
                        onChange={() => toggleQualification(item.trainingName, trainingTypes.METHOD, item.trainingId, item.qualificationTypeId, item.validForYears)} />
                      <ListItemText primary={item.trainingName} />
                    </ListItem>
                  ))}
              </List> */}
              </Collapse>
            </Box>
          </Box>
        ) : null}

        {/* Selected Qualifications Accordion */}

        <Box
          flex={1}
          style={{
            background: "#FFFFFF",
            borderRadius: "8px",
            border: "1px solid #ccc",
            padding: "12px 16px",
            height: "80vh",
            width: "max-content",
            overflowY: "auto",
          }}
        >
          <TextLabel
            type="titleRegular"
            gutterBottom
            sxStyles={{
              textAlign: "left",
              opacity: 1,
            }}
          >
            {t(
              "adminDashboard.form.addInstaller.labels.qualifications.qualificationFor",
            )}{" "}
            {getUserName()}
          </TextLabel>
          <Grid container spacing={2} marginBottom={2} marginTop={2}>
            <Grid item xs={6}>
              <FormControl
                fullWidth
                sx={{ backgroundColor: "#e7eaf6", borderRadius: 2 }}
                required
              >
                <StyledInputLabel id="passportTypeId-label">
                  {t(
                    "adminDashboard.form.addInstaller.labels.qualifications.passportType",
                  )}
                </StyledInputLabel>
                <Select
                  value={passportTypeId || ""}
                  onChange={handleChange}
                  displayEmpty
                  id="passportTypeId"
                  name="passportTypeId"
                  sx={{ height: "40px" }}
                  renderValue={(selected) =>
                    selected
                      ? passportTypes.find(
                          (type) => type.passportTypeId === selected,
                        )?.passportType
                      : ""
                  }
                >
                  {passportTypes.map((type) => (
                    <MenuItem
                      key={type.passportTypeId}
                      value={type.passportTypeId}
                    >
                      {type.passportType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              {passportTypeId ? (
                <FormControl
                  fullWidth
                  sx={{ backgroundColor: "#e7eaf6", borderRadius: 2 }}
                  required
                >
                  <StyledInputLabel>
                    {t(
                      "adminDashboard.form.addInstaller.labels.qualifications.qualificationType",
                    )}
                  </StyledInputLabel>
                  <Select
                    value={qualificationTypeId || ""}
                    onChange={handleChange}
                    id="qualificationType"
                    name="qualificationType"
                    sx={{ height: "40px" }}
                  >
                    {qualificationTypeList.map((qualType) => (
                      <MenuItem
                        key={qualType.qualificationTypeId}
                        value={qualType.qualificationTypeId}
                      >
                        {qualType.qualificationType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Grid>
          </Grid>

          {Boolean(passportTypeId) &&
            Boolean(qualificationTypeId) &&
            availableLevels.length > 0 && (
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  marginTop: 3,
                }}
              >
                <Tabs
                  value={activeLevelTab}
                  onChange={(_, v) => setActiveLevelTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 36,
                    "& .MuiTabs-indicator": { height: 2 },
                  }}
                >
                  <Tab
                    key="All"
                    value="All"
                    iconPosition="start"
                    icon={tabStatusIcon(getTabStatus("All")) || undefined}
                    label={t(
                      "adminDashboard.form.addInstaller.labels.qualifications.levelTabAll",
                    )}
                    sx={{
                      minHeight: 36,
                      paddingY: 0.5,
                      paddingX: 2,
                      textTransform: "none",
                      fontWeight: 500,
                      "& .MuiTab-iconWrapper": { marginRight: 0.5 },
                    }}
                  />
                  {availableLevels.map((level) => (
                    <Tab
                      key={level}
                      value={level}
                      iconPosition="start"
                      icon={tabStatusIcon(getTabStatus(level)) || undefined}
                      label={level}
                      sx={{
                        minHeight: 36,
                        paddingY: 0.5,
                        paddingX: 2,
                        textTransform: "none",
                        fontWeight: 500,
                        "& .MuiTab-iconWrapper": { marginRight: 0.5 },
                      }}
                    />
                  ))}
                </Tabs>
              </Box>
            )}

          <Box sx={{ flexGrow: 1, marginTop: 2 }}>
            {/* Column headers - aligned with row layout below */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                paddingBottom: 0.75,
                paddingRight: "32px", // reserves the delete-column slot
              }}
            >
              <Box sx={{ width: 28, flexShrink: 0 }} />
              <Box sx={{ flex: 1, paddingLeft: 2, minWidth: 0 }}>
                <TextLabel
                  key={"Name"}
                  type="tableHeader"
                  sxStyles={{
                    color: CONSTANTS.COLORS.TEXT_GRAY,
                    opacity: 1,
                    textAlign: "left",
                  }}
                >
                  {t("qualifications.labels.name")}
                </TextLabel>
              </Box>
              <Box sx={{ width: 125, flexShrink: 0 }}>
                <TextLabel
                  key={"Completion"}
                  type="tableHeader"
                  sxStyles={{
                    color: CONSTANTS.COLORS.TEXT_GRAY,
                    opacity: 1,
                    textAlign: "left",
                  }}
                >
                  {t("qualifications.labels.completion")}
                </TextLabel>
              </Box>
              <Box sx={{ width: 142, flexShrink: 0, paddingLeft: 2 }}>
                <TextLabel
                  key={"Expiration"}
                  type="tableHeader"
                  sxStyles={{
                    color: CONSTANTS.COLORS.TEXT_GRAY,
                    opacity: 1,
                    textAlign: "left",
                  }}
                >
                  {t("qualifications.labels.expiration")}
                </TextLabel>
              </Box>
            </Box>

            {visibleQualifications.map((qualification, idx, arr) => {
              if (!qualification) return null;
              const prev = idx > 0 ? arr[idx - 1] : null;
              const isFirstInGroup = !prev || prev.type !== qualification.type;
              const groupGap = idx === 0 ? 0 : isFirstInGroup ? 3.5 : 0.75;
              const typePalette =
                qualification.type === trainingTypes.GLOBAL_SAFETY
                  ? TYPE_COLORS.GLOBAL_SAFETY
                  : qualification.type === trainingTypes.METHOD
                    ? TYPE_COLORS.METHOD
                    : TYPE_COLORS.LOCAL_SAFETY;
              const statusSrc =
                qualification.state === "Warning"
                  ? "/icon_expiringsoon.svg"
                  : qualification.state === "Expired"
                    ? "/icon_expired.svg"
                    : qualification.state === "Active"
                      ? "/icon_valid.svg"
                      : null;
              const completionError =
                validationErrors.completionDate &&
                validationErrors.completionDate[qualification.trainingId];
              const expirationError =
                validationErrors.expirationDate &&
                validationErrors.expirationDate[qualification.trainingId];
              const rowError = completionError || expirationError;
              return (
                <Box
                  key={qualification.trainingId}
                  sx={{ marginTop: groupGap }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {/* Status icon - fixed slim slot, sits just left of the pill */}
                    <Box
                      sx={{
                        width: 28,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {statusSrc && (
                        <img
                          src={statusSrc}
                          alt={qualification.state || ""}
                          style={{ width: 18, height: 18 }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: typePalette.bg,
                        border: `1px solid ${typePalette.border}`,
                        borderRadius: 2,
                        paddingY: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          paddingLeft: 2,
                          paddingRight: 1,
                        }}
                      >
                        <TextLabel
                          type="regularText"
                          style={{
                            textAlign: "left",
                            opacity: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {qualification.title}
                        </TextLabel>
                      </Box>
                      <Box sx={{ width: 125, flexShrink: 0 }}>
                        <TextField
                          type="date"
                          value={
                            qualification.completionDate ||
                            new Date().toISOString().split("T")[0]
                          }
                          onChange={(e) =>
                            handleDateChange(
                              qualification.trainingId,
                              "completionDate",
                              e.target.value,
                            )
                          }
                          variant="standard"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ disableUnderline: true }}
                          sx={{
                            '& input[type="date"]': {
                              width: 118,
                              minWidth: 118,
                              padding: 0,
                            },
                          }}
                          error={!!completionError}
                          required
                        />
                      </Box>
                      <Box
                        sx={{
                          width: 142,
                          flexShrink: 0,
                          paddingLeft: 2,
                          paddingRight: 0.5,
                        }}
                      >
                        <TextField
                          type="date"
                          value={qualification.expirationDate || ""}
                          onChange={(e) =>
                            handleDateChange(
                              qualification.trainingId,
                              "expirationDate",
                              e.target.value,
                            )
                          }
                          variant="standard"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ disableUnderline: true }}
                          sx={{
                            '& input[type="date"]': {
                              width: 118,
                              minWidth: 118,
                              padding: 0,
                            },
                          }}
                          error={!!expirationError}
                          required
                        />
                      </Box>
                    </Box>

                    {/* Delete - outside the pill, tight slot */}
                    <Box
                      sx={{
                        width: 32,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{ padding: 0.5 }}
                        onClick={() =>
                          handleDeleteQualification(qualification.trainingId)
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  {rowError && (
                    <Box
                      sx={{
                        paddingRight: "40px",
                        paddingTop: 0.25,
                        textAlign: "right",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "error.main", lineHeight: 1.3 }}
                      >
                        {rowError}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center", // Center the buttons horizontally
              gap: 2, // Add spacing between the buttons
              mt: 2, // Add margin-top
            }}
          >
            <StyledButtons.SecondaryButton onClick={handleCancel}>
              {t("adminDashboard.form.button.cancel")}
            </StyledButtons.SecondaryButton>
            <StyledButtons.PrimaryButton type="submit" onClick={handleSubmit}>
              {t("adminDashboard.form.button.save")}
            </StyledButtons.PrimaryButton>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default QualificationsApp;
